/** @format — trucking.quote AG-UI card (Figma 220px layout, light border) */

import React from "react";
import { Truck, Globe, Calendar, Ship, Clock } from "lucide-react";
import PortBadge from "./PortBadge";
import { PORT_BADGE_IDS } from "./portBadgeUtils";

function formatMoney(n, currency = "USD", fractionDigits = 0) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(Number(n));
}

function formatDateShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

/** Short route line per Figma */
function formatRouteShort(props) {
  if (props.routeLabel) return props.routeLabel;
  const origin = props.origin?.split(",")[0]?.trim() ?? props.origin;
  const dest = props.destination?.split(",")[0]?.trim() ?? props.destination;
  if (origin && dest) return `${origin} to ${dest}`;
  return props.title ?? "Trucking quote";
}

function hasStructuredAdmin(admin) {
  if (!admin || typeof admin !== "object") return false;
  return (
    admin.wholesale_price != null ||
    admin.suggested_retail != null ||
    admin.minimum_safe_customer_price != null ||
    admin.market_high != null ||
    admin.market_mean != null ||
    admin.markup_amount != null ||
    admin.markup_percentage != null
  );
}

function BreakdownRow({ label, value, boldValue = false }) {
  return (
    <div className="flex gap-3 justify-between items-center">
      <span className="text-[10px] leading-none text-[#475367]">{label}</span>
      <span
        className={`text-[10px] leading-none text-[#1d2739] tabular-nums ${
          boldValue ? "font-semibold text-[#0a0a0a]" : "font-semibold"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function AdminPricingSection({ admin, currency, customerPrice }) {
  const wholesale = admin.wholesale_price;
  const marketLow = admin.market_mean;
  const marketHigh = admin.market_high;
  const floor = admin.minimum_safe_customer_price;
  const markupAmount = admin.markup_amount;
  const markupPct = admin.markup_percentage;

  const marketRange =
    marketLow != null && marketHigh != null
      ? `${formatMoney(marketLow, currency, 0)} – ${formatMoney(marketHigh, currency, 0)}`
      : marketHigh != null
        ? formatMoney(marketHigh, currency, 0)
        : marketLow != null
          ? formatMoney(marketLow, currency, 0)
          : null;

  const profitLabel =
    markupAmount != null && markupPct != null
      ? `${formatMoney(markupAmount, currency, 0)} (${Number(markupPct).toFixed(0)}%)`
      : markupAmount != null
        ? formatMoney(markupAmount, currency, 0)
        : markupPct != null
          ? `${Number(markupPct).toFixed(0)}%`
          : null;

  return (
    <div className="flex w-full flex-col gap-1.5" data-testid="trucking-quote-admin-pricing">
      <p className="text-sm font-semibold leading-none text-[#0a0a0a] tabular-nums">
        {formatMoney(customerPrice, currency, 0)}
      </p>

      <div className="flex flex-col gap-2 px-2 py-2 rounded bg-[#F9FAFB] border border-[#e4e7ec]">
        <p className="text-[10px] leading-none font-bold text-[#475367]">Admin pricing</p>
        <div className="flex flex-col gap-2">
          {wholesale != null ? <BreakdownRow label="Your cost" value={formatMoney(wholesale, currency, 0)} /> : null}
          {marketRange ? <BreakdownRow label="Market range" value={marketRange} /> : null}
          {floor != null ? <BreakdownRow label="Floor" value={formatMoney(floor, currency, 0)} /> : null}
          {profitLabel ? <BreakdownRow label="Profit" value={profitLabel} boldValue /> : null}
        </div>
        {/* {floor != null ? (
          <p className="text-[10px] leading-snug text-[#667185] pt-1 border-t border-[#e4e7ec]">
            Do not quote below the floor.
          </p>
        ) : null} */}
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 min-h-[20px]">
      <span className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
        <Icon className="w-3 h-3 text-[#737373]" strokeWidth={1.5} aria-hidden />
      </span>
      <span className="text-xs leading-none text-[#1d2739]">{children}</span>
    </div>
  );
}

function TruckingOnlyBadge() {
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-stone-100 border border-neutral-200">
      <Truck className="flex-shrink-0 w-3 h-3 text-stone-700" strokeWidth={1.5} aria-hidden />
      <span className="text-xs font-medium text-center whitespace-nowrap text-stone-700">Trucking only</span>
    </div>
  );
}

function TruckingOceanBadges() {
  return (
    <div className="flex flex-wrap items-start gap-1.5">
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-stone-100 border border-neutral-200">
        <Globe className="flex-shrink-0 w-3 h-3 text-stone-700" strokeWidth={1.5} aria-hidden />
        <span className="text-xs font-medium leading-none whitespace-nowrap text-stone-700">Trucking + Ocean</span>
      </div>
      <PortBadge badgeId={PORT_BADGE_IDS.BEST_CHOICE} />
    </div>
  );
}

export default function TruckingQuoteCard({ componentId, props = {}, onAction, disabled }) {
  const currency = props.currency ?? "USD";
  const isOceanCombo = props.variant === "trucking_ocean" || props.mode === "trucking_ocean";
  const routeLabel = formatRouteShort(props);

  const primaryAction = props.actions?.find((a) => a.kind === "primary") ?? props.actions?.[0];
  const secondaryAction = props.actions?.find((a) => a.kind === "secondary") ?? props.actions?.[1];

  const fireAction = (actionId, e) => {
    e?.stopPropagation?.();
    const action = props.actions?.find((a) => a.id === actionId);
    onAction?.({
      componentId,
      actionId,
      label: action?.label ?? actionId,
      value: null,
    });
  };

  const admin = props.admin ?? {};
  const showAdminPricingBlock = hasStructuredAdmin(admin);

  const isLegacyAdminHints =
    !showAdminPricingBlock && (props.customerPrice != null || props.minCustomerPrice != null || props.yourCost != null);

  const isAdmin = showAdminPricingBlock || isLegacyAdminHints;

  const customerPrice = props.customerPrice ?? admin.suggested_retail ?? null;
  const yourCost = props.yourCost ?? admin.wholesale_price ?? props.truckingCost ?? props.totalCost ?? null;
  const minCustomerPrice = props.minCustomerPrice ?? admin.minimum_safe_customer_price ?? null;

  const headlinePrice = isAdmin ? (customerPrice ?? yourCost) : (props.truckingCost ?? props.totalCost);

  const primaryLabel = primaryAction?.label ?? (isOceanCombo ? "Ship overseas" : "Book this trucking");

  return (
    <div className="overflow-visible pt-1 w-full min-w-0">
      <div className="flex flex-col justify-center items-start w-[220px] max-w-full gap-2 p-3 rounded-lg bg-white border border-[#e4e7ec] transition-shadow duration-200 hover:shadow-md">
        {isOceanCombo ? <TruckingOceanBadges /> : <TruckingOnlyBadge />}

        <div className="flex flex-col gap-2 justify-start items-start self-stretch">
          <div className="flex flex-col gap-1 justify-center items-start self-stretch">
            <p className="text-xs font-medium text-left text-[#11121a] line-clamp-2 leading-snug">{routeLabel}</p>
            <div className="flex flex-col gap-1 justify-center items-start self-stretch">
              {showAdminPricingBlock ? (
                <AdminPricingSection admin={admin} currency={currency} customerPrice={headlinePrice} />
              ) : (
                <>
                  {isAdmin ? <span className="text-[10px] font-medium text-neutral-500">Show customer</span> : null}
                  <p className="self-stretch text-sm font-semibold text-left text-[#0a0a0a] tabular-nums">
                    {formatMoney(headlinePrice, currency, 0)}
                  </p>
                </>
              )}
              {!showAdminPricingBlock && isLegacyAdminHints ? (
                <div className="flex flex-col gap-0.5 pt-0.5">
                  {yourCost != null ? (
                    <p className="text-[10px] text-left text-neutral-500 tabular-nums">
                      Your cost {formatMoney(yourCost, currency, 0)}
                    </p>
                  ) : null}
                  {minCustomerPrice != null ? (
                    <p className="text-[10px] text-left text-neutral-500 tabular-nums">
                      Floor {formatMoney(minCustomerPrice, currency, 0)} · never quote below
                    </p>
                  ) : null}
                </div>
              ) : null}
              {props.etaDate || props.eta ? (
                <p className="text-[10px] text-left text-neutral-500">
                  ETA {formatDateShort(props.etaDate) ?? props.eta}
                </p>
              ) : null}
            </div>
          </div>

          {isOceanCombo ? (
            <div className="flex flex-col gap-1.5">
              {props.pickupDate ? (
                <DetailRow icon={Calendar}>Pickup {formatDateShort(props.pickupDate)}</DetailRow>
              ) : null}
              {props.portLabel ? <DetailRow icon={Ship}>{props.portLabel}</DetailRow> : null}
              {props.etaDate ? <DetailRow icon={Clock}>ETA {formatDateShort(props.etaDate)}</DetailRow> : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 justify-center items-start self-stretch pt-2">
          {primaryAction ? (
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => fireAction(primaryAction.id, e)}
              className="flex justify-center items-center self-stretch gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-neutral-300 text-sm font-medium text-center text-[#0a0a0a] hover:bg-[#eef4fc] hover:border-[#98A2B3] disabled:opacity-50 transition-colors min-h-[32px]"
            >
              <span className="max-w-full truncate">{primaryLabel}</span>
            </button>
          ) : null}

          {props.suggestOcean && secondaryAction ? (
            <div className="flex flex-col gap-1 justify-center items-center self-stretch">
              <p className="text-[10px] text-center text-neutral-500">Heading overseas eventually?</p>
              <button
                type="button"
                disabled={disabled}
                onClick={(e) => fireAction(secondaryAction.id, e)}
                className="flex justify-center items-center self-stretch gap-1.5 px-2 py-[3px] rounded-full bg-transparent text-xs font-medium text-center text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50 whitespace-nowrap"
              >
                Check ocean rates
              </button>
            </div>
          ) : null}

          {props.note && !props.suggestOcean ? (
            <p className="text-[10px] leading-relaxed text-neutral-500">{props.note}</p>
          ) : null}

          {props.footnote ? <p className="text-[10px] leading-relaxed text-neutral-500">{props.footnote}</p> : null}
        </div>
      </div>
    </div>
  );
}
